// AttachmentInput.tsx
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import AttachmentPreviewModal from "../../pages/Communications/components/CommunicationDetailsPanel/components/AttachmentPreviewModal";
import { AttachmentPreviewItem } from "../../pages/Communications/utils/types";
import { customToast } from "../../utils/custom-toast/custom-toast";
import AttachmentCarousel from "./AttachmentCarousel";
import AttachmentInputCarousel from "./AttachmentInputCarousel";

export type AttachmentItem = {
  id: string;
  file: File | null; // null for placeholder items that were created from remote urls
  name: string;
  size?: number;
  mime?: string;
  previewUrl?: string; // created via URL.createObjectURL for images or provided for remote items
  status?: "pending" | "uploading" | "sent" | "failed";
  progress?: number; // 0-100
  remoteUrl?: string; // url after upload
  s3Url?: string; // full s3 url after upload
};

type Props = {
  value?: AttachmentItem[]; // controlled list (optional)
  onChange?: (items: AttachmentItem[]) => void;
  accept?: string; // file input accept attribute
  multiple?: boolean;
  maxFiles?: number; // maximum number of files allowed (default: 5)
  onFilePickerClose?: () => void; // called after file picker closes (files selected or cancelled)
  variant?: "default" | "compact";
  allowExcel?: boolean; // when true, also accept Excel files (.xls/.xlsx); default false
};

export type AttachmentInputRef = {
  triggerFilePicker: () => void;
  addFiles: (files: FileList | null) => void;
};

function fileExt(name = "") {
  const m = name.split(".");
  return m.length > 1 ? m[m.length - 1].toLowerCase() : "";
}

function isImageMime(mime?: string) {
  return !!mime && mime.startsWith("image/");
}

// Excel MIME types: .xls (legacy) and .xlsx (OpenXML spreadsheet)
const EXCEL_MIME_TYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

function isValidFileType(file: File, allowExcel = false): boolean {
  const ext = fileExt(file.name).toLowerCase();
  const mime = file.type.toLowerCase();

  // Check by extension
  const validExts = ["pdf", "doc", "docx", "csv"];
  if (allowExcel) validExts.push("xls", "xlsx");
  if (validExts.includes(ext)) return true;

  // Check by MIME type
  if (mime.startsWith("image/")) return true;
  if (mime === "application/pdf") return true;
  if (mime === "application/msword") return true;
  if (
    mime ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
    return true;
  if (mime === "text/csv" || mime === "text/comma-separated-values")
    return true;
  if (allowExcel && EXCEL_MIME_TYPES.includes(mime)) return true;

  return false;
}

const AttachmentInput = forwardRef<AttachmentInputRef, Props>(
  (
    {
      value = [],
      onChange,
      accept = "image/*,.pdf,.doc,.docx,.csv",
      multiple = true,
      maxFiles = 5,
      onFilePickerClose,
      variant,
      allowExcel = false,
    },
    ref
  ) => {
    const [items, setItems] = useState<AttachmentItem[]>(value);
    const inputFileRef = useRef<HTMLInputElement>(null);
    const itemsRef = useRef(items);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewIndex, setPreviewIndex] = useState<number>(0);
    const previewUrlsRef = useRef<Map<string, string>>(new Map());
    // True when setItems is called from the controlled-value sync (not a user action).
    // Prevents the onChange effect from firing back to the parent for parent-driven
    // updates, which would create a feedback loop and allow isUpdatingRef to block
    // user-initiated removes.
    const isValueSyncRef = useRef(false);

    // keep ref in sync (used to revoke previews on unmount)
    useEffect(() => {
      itemsRef.current = items;
    }, [items]);

    // sync controlled value — mark the update so the onChange effect skips it
    useEffect(() => {
      if (value !== undefined) {
        isValueSyncRef.current = true;
        setItems(value);
      }
    }, [value]);

    useEffect(() => {
      if (isValueSyncRef.current) {
        isValueSyncRef.current = false;
        return;
      }
      onChange?.(items);
    }, [items, onChange]);

    // revoke previews on unmount to avoid memory leak
    useEffect(() => {
      const previewUrls = previewUrlsRef.current;
      return () => {
        itemsRef.current.forEach((it) => {
          if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
        });
        // Clean up preview URLs
        previewUrls.forEach((url) => {
          URL.revokeObjectURL(url);
        });
        previewUrls.clear();
      };
    }, []);

    function handleAddFiles(files: FileList | null) {
      if (!files) return;
      // Use functional update to get the most current state
      const currentCount = items.length;
      const remainingSlots = maxFiles - currentCount;
      if (remainingSlots <= 0) {
        customToast(`Maximum ${maxFiles} file(s) allowed`, "warning", 3000);
        return items;
      }
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
      const fileArray = Array.from(files);
      const oversizedFiles = fileArray.filter((f) => f.size > MAX_FILE_SIZE);
      if (oversizedFiles.length > 0) {
        oversizedFiles.forEach((f) => {
          customToast(
            `"${f.name}" exceeds the 10 MB size limit and was not added`,
            "error",
            4000
          );
        });
      }
      const sizedFiles = fileArray.filter((f) => f.size <= MAX_FILE_SIZE);
      const validFiles = sizedFiles.filter((f) => isValidFileType(f, allowExcel));
      const invalidFiles = sizedFiles.filter(
        (f) => !isValidFileType(f, allowExcel)
      );

      // Show warning for invalid files
      if (invalidFiles.length > 0) {
        customToast(
          `Invalid file type(s) ignored`,
          "warning",
          3000,
          allowExcel
            ? `Only images, PDF, DOC, CSV, and Excel files are allowed.`
            : `Only images, PDF, DOC, and CSV files are allowed.`
        );
      }

      // Filter out duplicate files (by name and size) - silently
      const existingFileKeys = new Set(
        items.map((item) => `${item.name}_${item.size}`)
      );
      const uniqueFiles = validFiles.filter((file) => {
        const fileKey = `${file.name}_${file.size}`;
        return !existingFileKeys.has(fileKey);
      });

      const filesToAdd = uniqueFiles.slice(0, remainingSlots);
      const skippedCount = uniqueFiles.length - filesToAdd.length;
      // Show warning if files were skipped due to max limit
      if (skippedCount > 0) {
        customToast(
          `Only ${remainingSlots} file(s) can be added`,
          "warning",
          3000,
          `${skippedCount} file(s) skipped. Maximum ${maxFiles} file(s) allowed.`
        );
      }
      if (filesToAdd.length === 0) {
        return items;
      }
      const newItems: AttachmentItem[] = filesToAdd.map((f) => ({
        id: `att_${Math.random().toString(36).slice(2, 9)}`,
        file: f,
        name: f.name,
        size: f.size,
        mime: f.type,
        previewUrl: f.type.startsWith("image/")
          ? URL.createObjectURL(f)
          : undefined,
        status: "pending",
        progress: 0,
      }));
      const newItemsArray = [...items, ...newItems];
      setItems(newItemsArray);
    }

    function handleRemoveAttachment(id: string) {
      setItems((prev) => {
        const toRemove = prev.find((x) => x.id === id);
        if (toRemove && toRemove.previewUrl)
          URL.revokeObjectURL(toRemove.previewUrl);
        return prev.filter((x) => x.id !== id);
      });
    }

    // Convert AttachmentItem[] to AttachmentPreviewItem[] for the preview modal
    const getPreviewAttachments = useCallback((): AttachmentPreviewItem[] => {
      return items.map((item) => {
        // Prioritize existing previewUrl (already created for images)
        let url = item.previewUrl;

        // If no previewUrl, check the ref
        if (!url) {
          url = previewUrlsRef.current.get(item.id);
        }

        // If still no URL, create one from the file
        if (!url && item.file) {
          url = URL.createObjectURL(item.file);
          previewUrlsRef.current.set(item.id, url);
        }

        // Final fallback: prefer the full s3Url over the s3_key remoteUrl
        if (!url) {
          url = item.s3Url || item.remoteUrl || "";
        }

        // For images, use the same URL for both s3_url and thumbnail_url
        const imageUrl = isImageMime(item.mime) ? url : undefined;

        return {
          s3_url: url || "",
          file_name: item.name,
          content_type: item.mime,
          thumbnail_url: imageUrl,
          timestamp: "",
        };
      });
    }, [items]);

    function handlePreviewAttachment(item: AttachmentItem, index?: number) {
      if (!item.file && !item.remoteUrl && !item.previewUrl) {
        console.warn("Cannot preview: no file or URL available", item);
        return;
      }

      const itemIndex =
        index !== undefined
          ? index
          : items.findIndex((it) => it.id === item.id);
      setPreviewIndex(itemIndex >= 0 ? itemIndex : 0);
      setIsPreviewOpen(true);
    }

    function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
      handleAddFiles(e.target.files);
      if (inputFileRef.current) inputFileRef.current.value = "";
      // Call onFilePickerClose after a short delay to ensure it's called after the file picker closes
      setTimeout(() => {
        onFilePickerClose?.();
      }, 0);
    }

    const triggerFilePicker = useCallback(() => {
      inputFileRef.current?.click();
    }, []);

    useImperativeHandle(ref, () => ({
      triggerFilePicker,
      addFiles: handleAddFiles,
    }));

    return (
      <>
        <input
          ref={inputFileRef}
          className="hidden"
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileInputChange}
        />
        <AttachmentInputCarousel
          items={items}
          onPreview={handlePreviewAttachment}
          onRemove={handleRemoveAttachment}
          variant={variant}
        />
        <AttachmentPreviewModal
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          attachments={getPreviewAttachments()}
          initialIndex={previewIndex}
        />
      </>
    );
  }
);

AttachmentInput.displayName = "AttachmentInput";

export default AttachmentInput;
